import { Transaction } from "@/models/Transaction";
import Toast from "@/plugins/Toast";
import TransactionRepository from "@/repositories/TransactionRepository";
import ToolsService from "@/services/ToolsService";
import { useAuthStore } from "@/store/auth.store";
import { ToastType } from "@/types/toast";
import { AxiosResponse } from "axios";
import { computed, inject, ref, watch } from "vue";
import { useDisplay } from "vuetify/lib/framework.mjs";

export default class TransactionViewModel {
  public breadcrumbs = [
    { title: "Dashboard", disabled: false, href: "/app/home" },
    { title: "Movimientos", disabled: true, href: "" },
  ];
  private toast: Toast;
  public tab = ref<string>("aportes"); // Active tab
  public searchAportes = ref<string>(""); // Aportes search input
  public searchCreditos = ref<string>(""); // Créditos search input
  public searchAhorros = ref<string>(""); // Ahorros search input
  public loading = ref<boolean>(false);

  // Reactive transaction data for each tab
  public contribTransactions = ref<Transaction[]>([]);
  public creditTransactions = ref<Transaction[]>([]);
  public saverTransactions = ref<Transaction[]>([]);

  // Paginación
  public currentPageAportes = ref<number>(1);
  public currentPageCreditos = ref<number>(1);
  public currentPageAhorros = ref<number>(1);
  public pageSize = 20;

  // Datos paginados
  public paginatedAportes = computed(() =>
    this.contribTransactions.value.slice(
      0,
      this.currentPageAportes.value * this.pageSize
    )
  );
  public paginatedCreditos = computed(() =>
    this.creditTransactions.value.slice(
      0,
      this.currentPageCreditos.value * this.pageSize
    )
  );
  public paginatedAhorros = computed(() =>
    this.saverTransactions.value.slice(
      0,
      this.currentPageAhorros.value * this.pageSize
    )
  );

  public authStore = useAuthStore();
  public display = useDisplay();

  private toolsService = new ToolsService();

  public isVertical = computed(() => this.display.lgAndUp.value);

  constructor() {
    this.toast = inject("toast") as Toast;

    // Verificar cuando cambia la pestaña y reiniciar la paginación
    watch(this.tab, () => {
      this.resetPagination();
    });

    this.fetchTransactions();
  }

  async fetchTransactions() {
    try {
      this.loading.value = true;
      const authBasic = btoa(
        `${this.authStore.getLogin}:${this.authStore.getDecryptedPassword}`
      );
      const response: AxiosResponse = await TransactionRepository.transactions(
        authBasic
      );

      const { data } = response.data;

      // Assign transactions to their respective tabs
      this.contribTransactions.value = data.contrib_transactions || [];
      this.creditTransactions.value = data.credit_transactions || [];
      this.saverTransactions.value = data.saver_transactions || [];

      this.loading.value = false;
    } catch (error) {
      console.error("Error fetching transactions:", error);
      this.toast.showToast(
        "Error al cargar las transacciones. Intente nuevamente.",
        ToastType.Error
      );
      this.loading.value = false;
    }
  }

  // Métodos para cargar más datos en paginación
  loadMoreAportes() {
    if (
      this.currentPageAportes.value * this.pageSize <
      this.contribTransactions.value.length
    ) {
      this.currentPageAportes.value++;
    }
  }

  loadMoreCreditos() {
    if (
      this.currentPageCreditos.value * this.pageSize <
      this.creditTransactions.value.length
    ) {
      this.currentPageCreditos.value++;
    }
  }

  loadMoreAhorros() {
    if (
      this.currentPageAhorros.value * this.pageSize <
      this.saverTransactions.value.length
    ) {
      this.currentPageAhorros.value++;
    }
  }

  // Resetear la paginación al cambiar de pestaña
  resetPagination() {
    this.currentPageAportes.value = 1;
    this.currentPageCreditos.value = 1;
    this.currentPageAhorros.value = 1;
  }

  // Computed properties for filtered transactions
  public filteredContribTransactions = computed(() =>
    this.filterTransactions(
      this.searchAportes.value,
      this.contribTransactions.value
    )
  );

  public filteredCreditTransactions = computed(() =>
    this.filterTransactions(
      this.searchCreditos.value,
      this.creditTransactions.value
    )
  );

  public filteredSaverTransactions = computed(() =>
    this.filterTransactions(
      this.searchAhorros.value,
      this.saverTransactions.value
    )
  );

  // Utility function to filter transactions
  private filterTransactions(
    search: string,
    transactions: Transaction[]
  ): Transaction[] {
    if (!search.trim()) {
      // Si no hay búsqueda, mostrar los datos paginados
      if (transactions === this.contribTransactions.value)
        return this.paginatedAportes.value;
      if (transactions === this.creditTransactions.value)
        return this.paginatedCreditos.value;
      return this.paginatedAhorros.value;
    }
    // Mostrar todos los resultados que coincidan
    const lowerCaseSearch = search.toLowerCase();
    return transactions.filter(
      (transaction) =>
        transaction.concepto.toLowerCase().includes(lowerCaseSearch) ||
        transaction.fecha.toLowerCase().includes(lowerCaseSearch) ||
        transaction.saldo.toString().includes(lowerCaseSearch)
    );
  }

  // Clear search input for each tab
  clearSearchAportes() {
    this.searchAportes.value = "";
  }

  clearSearchCreditos() {
    this.searchCreditos.value = "";
  }

  clearSearchAhorros() {
    this.searchAhorros.value = "";
  }
  /**
   * Format currency based on coin type.
   */
  formatCoin(coin: number): string {
    return this.toolsService.getCoin(coin);
  }
  /**
   * Format float numbers with localization.
   */
  formatFloat(number: number): string {
    return this.toolsService.getFloat(number);
  }
  getColor(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "success"; // Verde
      case "2": // Movimiento de retiro
        return "error"; // Rojo
      default: // Otro tipo de movimiento
        return "primary"; // Color predeterminado
    }
  }

  getIconName(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "mdi-arrow-top-right"; // Flecha hacia arriba
      case "2": // Movimiento de retiro
        return "mdi-arrow-bottom-left"; // Flecha hacia abajo
      default: // Otro tipo de movimiento
        return "mdi-wallet"; // Ícono genérico
    }
  }
}
